import type { ReactNode } from "react";
import { AlertOctagon, AlertTriangle, Info, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { formatAgo, formatStamp, useNow } from "../format";
import type { EventLevel, Severity } from "../types";

/** A titled block of the board (card with a compact header and an optional action area). */
export function SectionCard({
  title,
  description,
  actions,
  children,
  className,
  contentClassName,
  id,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  id?: string;
}) {
  return (
    <Card id={id} className={cn("min-w-0 overflow-hidden", className)}>
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2 border-b px-4 py-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold leading-tight">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <div className={cn("p-4", contentClassName)}>{children}</div>
    </Card>
  );
}

/** Relative time ("5 min ago"), ticking every second, absolute stamp on hover. */
export function Ago({ ts, className, fallback }: { ts: number | null | undefined; className?: string; fallback?: string }) {
  const now = useNow();
  if (!ts) return <span className={cn("text-muted-foreground", className)}>{fallback ?? "–"}</span>;
  return (
    <time dateTime={new Date(ts).toISOString()} title={formatStamp(ts)} className={cn("tabular-nums", className)}>
      {formatAgo(ts, now)}
    </time>
  );
}

const SEVERITY_STYLES: Record<Severity, { icon: typeof AlertOctagon; text: string; badge: string }> = {
  critical: {
    icon: AlertOctagon,
    text: "text-red-400",
    badge: "border-red-500/40 bg-red-500/15 text-red-300",
  },
  warning: {
    icon: AlertTriangle,
    text: "text-amber-400",
    badge: "border-amber-500/35 bg-transparent text-amber-300",
  },
  info: {
    icon: Info,
    text: "text-muted-foreground",
    badge: "border-border bg-transparent text-muted-foreground",
  },
};

export const levelToSeverity = (level: EventLevel): Severity =>
  level === "error" ? "critical" : level === "warning" ? "warning" : "info";

export function SeverityIcon({ severity, className }: { severity: Severity; className?: string }) {
  const { icon: Icon, text } = SEVERITY_STYLES[severity];
  return <Icon className={cn("h-4 w-4 shrink-0", text, className)} aria-hidden />;
}

export function SeverityBadge({ severity, label, className }: { severity: Severity; label: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-md border px-1.5 text-[10px] font-semibold uppercase tracking-wide",
        SEVERITY_STYLES[severity].badge,
        className,
      )}
    >
      {label}
    </span>
  );
}

/** Level badge of an event (error / warning / info) in the UI language. */
export function LevelBadge({ level, className }: { level: EventLevel; className?: string }) {
  const { t } = useLocalization();
  return <SeverityBadge severity={levelToSeverity(level)} label={t(`monitoring.level.${level}`)} className={className} />;
}

export function LoadingBlock({ height = 160, className }: { height?: number; className?: string }) {
  return <Skeleton className={cn("w-full rounded-md", className)} style={{ height }} />;
}

export function ErrorBlock({ message, onRetry, className }: { message: string; onRetry?: () => void; className?: string }) {
  const { t } = useLocalization();
  return (
    <div className={cn("flex flex-wrap items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300", className)}>
      <AlertOctagon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 break-words">{t("monitoring.loadFailed", { reason: message })}</span>
      {onRetry && (
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onRetry}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" />
          {t("monitoring.retry")}
        </Button>
      )}
    </div>
  );
}

export function EmptyBlock({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground", className)}>
      {children}
    </div>
  );
}

/** Small spinner shown while a section refetches in the background. */
export function FetchingDot({ active }: { active: boolean }) {
  if (!active) return null;
  return <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden />;
}

/** Label / value pairs in a responsive grid. */
export function KeyValueGrid({
  items,
  className,
  columns = "sm:grid-cols-2 xl:grid-cols-3",
}: {
  items: { label: ReactNode; value: ReactNode; hint?: string }[];
  className?: string;
  columns?: string;
}) {
  return (
    <dl className={cn("grid grid-cols-1 gap-x-6 gap-y-2", columns, className)}>
      {items.map((item, i) => (
        <div key={i} className="flex min-w-0 items-baseline justify-between gap-3 border-b border-border/50 py-1.5" title={item.hint}>
          <dt className="shrink-0 text-xs text-muted-foreground">{item.label}</dt>
          <dd className="min-w-0 break-words text-right font-mono text-xs tabular-nums [overflow-wrap:anywhere]">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Horizontal share bar (0..1), neutral unless a tone is given. */
export function Meter({ value, tone = "neutral", className }: { value: number | null | undefined; tone?: "neutral" | "warn" | "nok" | "info"; className?: string }) {
  const pct = Math.max(0, Math.min(1, value ?? 0)) * 100;
  const color = tone === "nok" ? "bg-red-500" : tone === "warn" ? "bg-amber-400" : tone === "info" ? "bg-sky-500/80" : "bg-muted-foreground/60";
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-muted", className)}>
      <div className={cn("h-full rounded-full", color)} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Tiny inline chart for table rows (SVG, no axes). */
export function Sparkline({
  values,
  color = "#8a8984",
  height = 22,
  bars = false,
  className,
  max,
}: {
  values: (number | null | undefined)[] | null | undefined;
  color?: string;
  height?: number;
  bars?: boolean;
  className?: string;
  max?: number;
}) {
  const data = (values ?? []).map((v) => (typeof v === "number" && Number.isFinite(v) ? v : null));
  if (!data.length) return <span className={cn("block text-xs text-muted-foreground", className)}>–</span>;
  const top = max ?? Math.max(1, ...data.map((v) => v ?? 0));
  const w = 100;
  const step = w / data.length;
  if (bars) {
    return (
      <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className={cn("block w-full", className)} style={{ height }} aria-hidden>
        {data.map((v, i) => {
          const h = v ? Math.max(1.5, (v / top) * (height - 1)) : 0;
          return <rect key={i} x={i * step + step * 0.12} width={step * 0.76} y={height - h} height={h} rx={0.8} fill={color} />;
        })}
      </svg>
    );
  }
  const points: string[] = [];
  data.forEach((v, i) => {
    if (v === null) return;
    points.push(`${(i + 0.5) * step},${height - 1 - (v / top) * (height - 2)}`);
  });
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className={cn("block w-full", className)} style={{ height }} aria-hidden>
      <polyline points={points.join(" ")} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

/** Pill of a live state: "OK" calm, problems in colour. */
export function StatePill({ tone, children, pulse = false, className }: { tone: "ok" | "warn" | "nok" | "neutral"; children: ReactNode; pulse?: boolean; className?: string }) {
  const styles = {
    ok: "border-green-500/25 text-green-400/90",
    warn: "border-amber-500/35 text-amber-300",
    nok: "border-red-500/40 bg-red-500/15 text-red-300",
    neutral: "border-border text-muted-foreground",
  }[tone];
  const dot = { ok: "bg-green-500/80", warn: "bg-amber-400", nok: "bg-red-500", neutral: "bg-muted-foreground/50" }[tone];
  return (
    <span className={cn("inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2 text-xs font-medium", styles, className)}>
      <span className="relative flex h-2 w-2">
        {pulse && <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-60", dot)} />}
        <span className={cn("relative inline-flex h-2 w-2 rounded-full", dot)} />
      </span>
      {children}
    </span>
  );
}
